import './InlineEdit.scss';

import { DataTypes, SchemaProperty, UOMS, UomTypes } from '@amzn/awscat-portfolio-assessment-types';
import { Box, Button, FormField, Grid, Input, SpaceBetween } from '@amzn/awsui-components-react';
import React, { useContext, useState } from 'react';

import { constants } from '../../../common/Localization';
import { DEFAULT_DATABASE_UOM, uomConversion } from '../../../common/uomConversion';
import { generateUomOptions, UOMOption } from '../utils';
import { InlineEditContext, InlineEditRowItem, InlineEditValue } from './InlineEditContext';
type Props = {
    cancelEdit: () => void;
    schemaProperty: SchemaProperty;
    originalValue: InlineEditValue;
    columnId: string;
    rowItem: InlineEditRowItem;
}

function InlineEditModal(props: Props) {
    const { inlineEditFunction } = useContext(InlineEditContext);
    const [ value, setValue ] = useState(props.originalValue);
    const [ selectedUOM, setSelectedUOM ] = useState<UOMS | null>(null);
    
    const onConfirmUpdate = async () => {
        let updatedValue = value;

        // Allow user to unset percentage properties
        if (props.schemaProperty?.UOM === UomTypes.PERCENTAGE && 
            (!['number', 'string'].includes(typeof(updatedValue)) || updatedValue === '')) {
            updatedValue = null;
        } else if (props.schemaProperty.UOM && selectedUOM) {
            const dbUOM = DEFAULT_DATABASE_UOM[props.schemaProperty.UOM];
            updatedValue = uomConversion(Number(value), selectedUOM as UOMS, dbUOM);
        }

        // Handle the corner case that original value is not in customUpdateOptions and users made no selections
        if (props.schemaProperty.customUpdateOptions) {
            updatedValue = (props.schemaProperty.customUpdateOptions.find(option => option.id === value) ?? props.schemaProperty.customUpdateOptions[0]).id;
        }

        props.cancelEdit();
        await inlineEditFunction(props.rowItem, props.columnId, updatedValue);
    };
    
    const buildUpdateForbiddenModal = (): JSX.Element => (<div>{props.schemaProperty.messages?.updateForbidden}</div>);

    const buildUpdateModal = (): JSX.Element => {
        const footer = (
            <Box float='right'>
                <SpaceBetween direction='horizontal' size='xs'>
                    <Button variant='link'
                        onClick={props.cancelEdit}>
                        {constants.CANCEL_BUTTON_TEXT}
                    </Button>
                    <Button
                        data-testid='btnSubmitInlineEditingModal'
                        variant='primary'
                        onClick={onConfirmUpdate}
                        disabled={false}>
                        {constants.UPDATE_BUTTON_TEXT}
                    </Button>
                </SpaceBetween>
            </Box>
        );

        let body: JSX.Element = (<></>);

        if (props.schemaProperty.UOM) {
            const uomOptions = generateUomOptions(props.schemaProperty.UOM);
            const originalUom = DEFAULT_DATABASE_UOM[props.schemaProperty.UOM];

            body = (
                <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]} >
                    <FormField>
                        <Input
                            type='number'
                            inputMode='numeric'
                            onChange={({ detail }) => setValue(detail.value)}
                            value={value?.toString() ?? ''} />
                    </FormField>
                    <FormField>
                        <select
                            className='inline-edit-select'
                            value={selectedUOM || originalUom}
                            onChange={(evt: React.ChangeEvent<HTMLSelectElement>) => setSelectedUOM(evt.target.value as UOMS)}
                        >
                            {uomOptions.map((uomOption: UOMOption) => {
                                return (
                                    <option key={uomOption.value} value={uomOption.label}>{uomOption.label}</option>
                                );
                            })}
                        </select>
                    </FormField>
                </Grid>
            );
        } else if (props.schemaProperty.dataType === DataTypes.BOOLEAN) {  
            body = (
                <FormField>
                    <select
                        className='inline-edit-select'
                        value={String(value)}
                        onChange={(evt: React.ChangeEvent<HTMLSelectElement>) => setValue(JSON.parse(evt.target.value))}
                    >
                        <option key={constants.TRUE} value={'true'}>{constants.TRUE}</option>
                        <option key={constants.FALSE} value={'false'}>{constants.FALSE}</option>
                    </select>
                </FormField>
            );
        } else if (props.schemaProperty.customUpdateOptions) {
            body = (
                <FormField>
                    <select
                        className='inline-edit-select'
                        value={String(value)}
                        onChange={(evt: React.ChangeEvent<HTMLSelectElement>) => setValue(evt.target.value)}
                    >
                        {props.schemaProperty.customUpdateOptions.map((customValues: { label: string; id: number | string }) => {
                            return (
                                <option key={customValues.id} value={customValues.id}>{customValues.label}</option>
                            );
                        })}
                    </select>
                </FormField>
            );
        } else {
            body = (
                <FormField>
                    <Input onChange={({ detail }) => setValue(detail.value)} value={value?.toString() ?? ''} />
                </FormField>
            );
        }

        return (
            <SpaceBetween size='l' data-testid='inlineEditModal'>
                {body}
                {footer}
            </SpaceBetween>
        );
    };
    return props.schemaProperty.messages?.updateForbidden ? buildUpdateForbiddenModal() : buildUpdateModal();
}

export default InlineEditModal;
